import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, Signal, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogService } from 'primeng/dynamicdialog';
import { SelectModule } from 'primeng/select';
import { TableModule, TableRowReorderEvent } from 'primeng/table';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { ManualMeasuresService } from '../../../../../../services/api/generated/manual-measures/manual-measures.service';
import { TableApiService } from '../../../../../../services/api/table.service';
import { UnitConversionsService } from '../../../../../../services/api/generated/unit-conversions/unit-conversions.service';
import { ModalHelperService } from '../../../../../../services/modal-helper.service';
import { PermissionFacadeService } from '../../../../../../services/permission-facade.service';
import { ToastService } from '../../../../../../services/toast.service';
import { MeasureCheckerStore } from '../../../../../../stores/measure-checker.store';
import { GraphMeasure } from '../../../../../../types/graph-data.interface';
import { MeasureSelectionResult } from '../../../../../../types/graph-dialog.interface';
import { CheckerType } from '../../../../../../types/measure-checker.interface';
import { DetailedManualMeasure } from '../../../../../../types/measure.interface';
import {
  CesTable,
  DEFAULT_DECIMALS,
  getGenreLabelKey,
  MeasureType,
  parseTableMeasureMetadata,
  TableMeasureConfig,
  TableMeasureRow,
} from '../../../../../../types/table.interface';
import { UnitConversionQueryResponse } from '../../../../../../services/api/generated/model';
import {
  canMoveDown as canMoveDownFn,
  canMoveUp as canMoveUpFn,
  moveItemsBy,
  moveItemsTo,
} from '../../../../../../utils/reorder.utils';
import { RouteUtils } from '../../../../../../utils/route.utils';
import { formatUnitLabel } from '../../../../../../utils/unit-select.utils';
import { CesButtonComponent } from '../../../../../ui/ces-buttons/ces-button.component';
import { CesButtonSecondaryComponent } from '../../../../../ui/ces-buttons/ces-button-secondary.component';
import { CesButtonTertiaryComponent } from '../../../../../ui/ces-buttons/ces-button-tertiary.component';
import { CesDeleteButtonComponent } from '../../../../../ui/ces-buttons/ces-delete-button.component';
import { CesFloatingBarComponent } from '../../../../../ui/ces-floating-bar/ces-floating-bar.component';
import { SelectMeasuresDialogComponent } from '../../../../../ui/dialogs/select-measures-dialog/select-measures-dialog.component';
import { CesGridSkeletonComponent } from '../../../../../ui/skeletons/grid-skeleton.component';

import { CesTooltipAutoDismissDirective } from '../../../../../../directives/ces-tooltip-auto-dismiss.directive';
const DECIMALS_OPTIONS = [
  { label: '0', value: 0 },
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5', value: 5 },
];

type BooleanConfigKey = 'showCalculatedValue' | 'showEnteredValue' | 'showVerificationValue';

interface BooleanColumn {
  readonly i18nKey: string;
  readonly configKey: BooleanConfigKey;
  readonly globalAriaKey: string;
  readonly global: Signal<boolean>;
  readonly applyGlobal: (value: boolean) => void;
  readonly isRowDisabled: (measure: TableMeasureRow) => boolean;
  readonly disabledTooltipKey: string;
}

@Component({
  selector: 'ces-table-config-tab',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col flex-1 min-h-0 overflow-y-auto' },
  imports: [
    CesTooltipAutoDismissDirective,
    FormsModule,
    TranslocoModule,
    ButtonModule,
    CheckboxModule,
    SelectModule,
    TableModule,
    ToggleSwitchModule,
    TooltipModule,
    CesButtonComponent,
    CesButtonSecondaryComponent,
    CesButtonTertiaryComponent,
    CesDeleteButtonComponent,
    CesFloatingBarComponent,
    CesGridSkeletonComponent,
  ],
  templateUrl: './table-config-tab.component.html',
})
export class TableConfigTabComponent {
  private readonly dialogService = inject(DialogService);
  private readonly modalHelper = inject(ModalHelperService);
  private readonly translate = inject(TranslocoService);
  private readonly manualMeasureService = inject(ManualMeasuresService);
  private readonly tableApiService = inject(TableApiService);
  private readonly measureCheckerStore = inject(MeasureCheckerStore);
  private readonly unitConversionService = inject(UnitConversionsService);
  private readonly toastService = inject(ToastService);
  private readonly permissionFacade = inject(PermissionFacadeService);

  readonly canWrite = this.permissionFacade.hasWriteAccess('analysis/tables/:id/config');
  readonly canReadMeasures = this.permissionFacade.hasReadAccess('configuration/measures');

  readonly table = input.required<CesTable>();
  readonly assetId = input.required<number>();
  readonly saved = input<boolean>(false);

  readonly dirtyChange = output<boolean>();
  readonly measuresChange = output<TableMeasureRow[]>();
  readonly save = output<TableMeasureRow[]>();

  readonly measures = signal<TableMeasureRow[]>([]);
  readonly isDirty = signal(false);

  readonly isLoading = signal(true);
  readonly decimalsOptions = DECIMALS_OPTIONS;
  readonly unitConversionsMap = signal<Map<number, UnitConversionQueryResponse[]>>(new Map());

  readonly hasSelection = computed(() => this.measures().some(m => m.selected));
  readonly selectedCount = computed(() => this.measures().filter(m => m.selected).length);
  readonly allSelected = computed(() => this.measures().length > 0 && this.measures().every(m => m.selected));

  readonly canMoveUp = computed(() => canMoveUpFn(this.measures()));
  readonly canMoveDown = computed(() => canMoveDownFn(this.measures()));

  readonly globalCalculatedValue = computed(() => {
    const m = this.measures();
    return m.length > 0 && m.every(row => row.config.showCalculatedValue);
  });

  readonly globalEnteredValue = computed(() => {
    const m = this.measures().filter(row => row.measureType !== MeasureType.ANALYSIS_POST);
    return m.length > 0 && m.every(row => row.config.showEnteredValue);
  });

  readonly globalVerificationValue = computed(() => {
    const m = this.measures().filter(row => row.hasPolynomial);
    return m.length > 0 && m.every(row => row.config.showVerificationValue);
  });

  readonly globalDecimals = computed(() => {
    const m = this.measures();
    const head = m[0];
    if (!head) return DEFAULT_DECIMALS;
    const first = head.config.decimals;
    return m.every(row => row.config.decimals === first) ? first : null;
  });
  readonly globalDecimalsIndeterminate = computed(() => this.globalDecimals() === null);

  private loadedTableId: number | null = null;
  private previousMeasureOrder: number[] = [];

  constructor() {
    effect(() => {
      if (this.saved()) {
        this.isDirty.set(false);
      }
    });

    effect(() => {
      const currentTable = this.table();
      if (currentTable && currentTable.id !== this.loadedTableId) {
        this.loadedTableId = currentTable.id;
        void this.loadTableMeasures(currentTable.id);
      }
    });
  }

  trackByMeasureId = (_: number, item: TableMeasureRow) => item.measureId;

  openSelectMeasuresDialog() {
    const currentAssetId = this.assetId();
    const preselectedIds = this.measures().map(m => m.measureId);

    const dialogRef = this.dialogService.open(
      SelectMeasuresDialogComponent,
      this.modalHelper.getFullscreenConfig({
        header: this.translate.translate('common.dialogs.selectMeasures.title'),
        data: {
          assetId: currentAssetId,
          preselectedIds,
        },
      }),
    );

    dialogRef.onClose.subscribe((result: MeasureSelectionResult | undefined) => {
      if (result) {
        void this.applyMeasureSelection(result.selectedMeasures, result.detailedMeasures);
      }
    });
  }

  private async applyMeasureSelection(selectedMeasures: GraphMeasure[], detailedMeasures?: DetailedManualMeasure[]) {
    const selectedIds = new Set(selectedMeasures.map(m => m.id));
    if (this.measures().some(m => !selectedIds.has(m.measureId))) {
      this.measures.update(measures => measures.filter(m => selectedIds.has(m.measureId)).map((m, i) => ({ ...m, sortNr: i + 1 })));
      this.markDirty();
    }
    await this.addMeasures(selectedMeasures, detailedMeasures);
  }

  toggleSelectAll(selected: boolean) {
    this.measures.update(measures => measures.map(m => ({ ...m, selected })));
  }

  applyGlobalCalculatedValue(value: boolean) {
    this.measures.update(measures => measures.map(m => ({ ...m, config: { ...m.config, showCalculatedValue: value } })));
    this.markDirty();
  }

  applyGlobalEnteredValue(value: boolean) {
    if (!this.measures().some(m => m.measureType !== MeasureType.ANALYSIS_POST)) return;
    this.measures.update(measures =>
      measures.map(m =>
        m.measureType === MeasureType.ANALYSIS_POST ? m : { ...m, config: { ...m.config, showEnteredValue: value } },
      ),
    );
    this.markDirty();
  }

  applyGlobalVerificationValue(value: boolean) {
    if (!this.measures().some(m => m.hasPolynomial)) return;
    this.measures.update(measures =>
      measures.map(m => (m.hasPolynomial ? { ...m, config: { ...m.config, showVerificationValue: value } } : m)),
    );
    this.markDirty();
  }

  applyGlobalDecimals(value: number | null) {
    if (value === null) return;
    this.measures.update(measures => measures.map(m => ({ ...m, config: { ...m.config, decimals: value } })));
    this.markDirty();
  }

  readonly booleanColumns: readonly BooleanColumn[] = [
    {
      i18nKey: 'calculatedValue',
      configKey: 'showCalculatedValue',
      globalAriaKey: 'tables.config.globalCalculatedValue',
      global: this.globalCalculatedValue,
      applyGlobal: v => this.applyGlobalCalculatedValue(v),
      isRowDisabled: () => false,
      disabledTooltipKey: '',
    },
    {
      i18nKey: 'enteredValue',
      configKey: 'showEnteredValue',
      globalAriaKey: 'tables.config.globalEnteredValue',
      global: this.globalEnteredValue,
      applyGlobal: v => this.applyGlobalEnteredValue(v),
      isRowDisabled: m => m.measureType === MeasureType.ANALYSIS_POST,
      disabledTooltipKey: 'common.tooltips.analysisPostOnly',
    },
    {
      i18nKey: 'verificationValue',
      configKey: 'showVerificationValue',
      globalAriaKey: 'tables.config.globalVerificationValue',
      global: this.globalVerificationValue,
      applyGlobal: v => this.applyGlobalVerificationValue(v),
      isRowDisabled: m => !m.hasPolynomial,
      disabledTooltipKey: 'common.tooltips.noPolynomial',
    },
  ];

  clearSelection() {
    this.toggleSelectAll(false);
  }

  toggleSelection(measure: TableMeasureRow, selected: boolean) {
    this.measures.update(measures => measures.map(m => (m.measureId === measure.measureId ? { ...m, selected } : m)));
  }

  updateConfig<K extends keyof TableMeasureConfig>(measure: TableMeasureRow, key: K, value: TableMeasureConfig[K]) {
    this.measures.update(measures =>
      measures.map(m => (m.measureId === measure.measureId ? { ...m, config: { ...m.config, [key]: value } } : m)),
    );
    this.markDirty();
  }

  moveUp() {
    this.moveSelectedBy(-1);
  }

  moveDown() {
    this.moveSelectedBy(1);
  }

  moveToTop() {
    this.moveSelectedTo('top');
  }

  moveToBottom() {
    this.moveSelectedTo('bottom');
  }

  private moveSelectedTo(position: 'top' | 'bottom') {
    this.measures.set(moveItemsTo(this.measures(), position).map((m, i) => ({ ...m, sortNr: i + 1 })));
    this.markDirty();
  }

  onRowReorder(event: TableRowReorderEvent) {
    if (event.dragIndex === undefined || event.dropIndex === undefined) return;

    const currentOrder = this.measures().map(m => m.measureId);
    const orderChanged =
      currentOrder.length !== this.previousMeasureOrder.length || currentOrder.some((id, i) => id !== this.previousMeasureOrder[i]);

    if (!orderChanged) return;

    this.previousMeasureOrder = currentOrder;
    this.measures.set(this.measures().map((m, i) => ({ ...m, sortNr: i + 1 })));
    this.markDirty();
  }

  deleteSelected() {
    this.measures.update(measures => measures.filter(m => !m.selected).map((m, i) => ({ ...m, sortNr: i + 1 })));
    this.markDirty();
  }

  deleteMeasure(measure: TableMeasureRow) {
    this.measures.update(measures => measures.filter(m => m.measureId !== measure.measureId).map((m, i) => ({ ...m, sortNr: i + 1 })));
    this.markDirty();
  }

  getMeasureLink(measure: TableMeasureRow): string {
    const assetId = this.assetId();
    const routeType = RouteUtils.getMeasureRouteSegment(measure.measureType);
    return `/assets/${assetId}/configuration/measures/${routeType}/${measure.measureId}/info`;
  }

  onSaveClick() {
    this.save.emit(this.measures());
  }

  private async loadTableMeasures(tableId: number) {
    this.isLoading.set(true);
    try {
      const tableMeasures = await firstValueFrom(this.tableApiService.getTableMeasures(tableId));
      if (!tableMeasures?.length) {
        this.measures.set([]);
        return;
      }

      const measureIds = tableMeasures.map(tm => tm.measureId);
      const currentAssetId = this.assetId();

      const measureDetailsResponse = await firstValueFrom(
        this.manualMeasureService.getDetailedMeasuresResource({
          assetId: currentAssetId,
          page: 1,
          pageSize: measureIds.length,
          filters: JSON.stringify([{ field: 'id', value: measureIds, matchMode: 'in' }]),
        }),
      );

      const measureDetails = (measureDetailsResponse?.measures ?? []) as unknown as DetailedManualMeasure[];
      const measureDetailsMap = new Map(measureDetails.map(m => [m.id, m]));

      const [checkersResults, conversionsMap] = await Promise.all([
        this.loadMeasureCheckers(measureIds),
        this.loadUnitConversions(measureDetails.map(m => m.unitId).filter((id): id is number => id != null)),
      ]);

      this.unitConversionsMap.set(conversionsMap);

      const rows: TableMeasureRow[] = tableMeasures
        .sort((a, b) => a.sortNr - b.sortNr)
        .map(tm => {
          const detail = measureDetailsMap.get(tm.measureId);
          const config = parseTableMeasureMetadata(tm.metadata);
          const measureType = detail?.type ?? 0;
          const measureUnit = detail?.unitSymbol ?? detail?.unitName ?? '';
          const measureUnitLabel = formatUnitLabel(detail?.unitName, detail?.unitSymbol);
          const measureUnitId = detail?.unitId ?? null;

          return {
            id: tm.id,
            sortNr: tm.sortNr,
            measureId: tm.measureId,
            measureName: detail?.name ?? `Mesure ${tm.measureId}`,
            measureCode: detail?.barcode ?? '',
            measureUnit,
            measureUnitId,
            measureType,
            measureGenre: this.translate.translate(getGenreLabelKey(measureType)),
            hasPolynomial: checkersResults.has(tm.measureId),
            config,
            selected: false,
            unitOptions: this.buildUnitOptions(measureUnitLabel, measureUnitId, conversionsMap),
          };
        });

      this.measures.set(rows);
      this.previousMeasureOrder = rows.map(r => r.measureId);
      this.isDirty.set(false);
    } catch (error) {
      console.error('Failed to load table measures:', error);
      this.toastService.error(this.translate.translate('common.toasts.operationFailed'));
    } finally {
      this.isLoading.set(false);
    }
  }

  private async loadMeasureCheckers(measureIds: number[]): Promise<Set<number>> {
    if (measureIds.length === 0) return new Set<number>();

    const checkers = await this.measureCheckerStore.getByMeasureIds(measureIds);
    const measureIdsWithPolynomial = new Set<number>();

    for (const checker of checkers) {
      if (
        checker.type === CheckerType.TWO_FIXED_LIMITS ||
        checker.type === CheckerType.TWO_POLYNOMIALS ||
        checker.type === CheckerType.POLYNOMIAL_AND_RANGES
      ) {
        measureIdsWithPolynomial.add(checker.measureId);
      }
    }

    return measureIdsWithPolynomial;
  }

  private async loadUnitConversions(unitIds: number[]): Promise<Map<number, UnitConversionQueryResponse[]>> {
    const uniqueUnitIds = [...new Set(unitIds)];
    if (uniqueUnitIds.length === 0) return new Map<number, UnitConversionQueryResponse[]>();

    const conversions = await firstValueFrom(
      this.unitConversionService.getUnitConversionDomainResource({ sourceUnitIds: uniqueUnitIds.join(',') }),
    );
    const conversionsMap = new Map<number, UnitConversionQueryResponse[]>();

    for (const conv of conversions) {
      const existing = conversionsMap.get(conv.srcUnitId) ?? [];
      existing.push(conv);
      conversionsMap.set(conv.srcUnitId, existing);
    }

    return conversionsMap;
  }

  private async addMeasures(newMeasures: GraphMeasure[], detailedMeasures?: DetailedManualMeasure[]) {
    const existing = this.measures();
    const existingIds = new Set(existing.map(m => m.measureId));
    const toAdd = newMeasures.filter(m => !existingIds.has(m.id));

    if (toAdd.length === 0) return;

    this.isLoading.set(true);

    try {
      const measureIds = toAdd.map(m => m.id);
      const measureDetails = detailedMeasures?.filter(d => measureIds.includes(d.id)) ?? [];
      const measureDetailsMap = new Map(measureDetails.map(m => [m.id, m]));

      const [checkersResults, newConversionsMap] = await Promise.all([
        this.loadMeasureCheckers(measureIds),
        this.loadUnitConversions(measureDetails.map(m => m.unitId).filter((id): id is number => id != null)),
      ]);

      const currentConversionsMap = this.unitConversionsMap();
      newConversionsMap.forEach((conversions, unitId) => {
        if (!currentConversionsMap.has(unitId)) {
          currentConversionsMap.set(unitId, conversions);
        }
      });
      this.unitConversionsMap.set(new Map(currentConversionsMap));

      const startSortNr = existing.length + 1;
      const mergedConversionsMap = this.unitConversionsMap();
      const newRows: TableMeasureRow[] = toAdd.map((m, i) => {
        const detail = measureDetailsMap.get(m.id);
        const measureType = detail?.type ?? 0;
        const measureUnit = detail?.unitSymbol ?? detail?.unitName ?? m.unit;
        const measureUnitLabel = formatUnitLabel(detail?.unitName, detail?.unitSymbol) || m.unit;
        const measureUnitId = detail?.unitId ?? null;
        return {
          id: 0,
          sortNr: startSortNr + i,
          measureId: m.id,
          measureName: m.name,
          measureCode: m.code,
          measureUnit,
          measureUnitId,
          measureType,
          measureGenre: this.translate.translate(getGenreLabelKey(measureType)),
          hasPolynomial: checkersResults.has(m.id),
          config: {
            showCalculatedValue: true,
            showEnteredValue: false,
            showVerificationValue: false,
            decimals: DEFAULT_DECIMALS,
            unitId: null,
          },
          selected: false,
          unitOptions: this.buildUnitOptions(measureUnitLabel, measureUnitId, mergedConversionsMap),
        };
      });

      this.measures.update(measures => [...measures, ...newRows]);
      this.markDirty();
    } finally {
      this.isLoading.set(false);
    }
  }

  private moveSelectedBy(delta: number) {
    this.measures.set(moveItemsBy(this.measures(), delta).map((m, i) => ({ ...m, sortNr: i + 1 })));
    this.markDirty();
  }

  private buildUnitOptions(
    measureUnitLabel: string,
    measureUnitId: number | null,
    conversionsMap: Map<number, UnitConversionQueryResponse[]>,
  ): { label: string; value: number | null }[] {
    const options: { label: string; value: number | null }[] = [{ label: measureUnitLabel, value: measureUnitId }];
    if (measureUnitId) {
      const conversions = conversionsMap.get(measureUnitId) ?? [];
      for (const conv of conversions) {
        options.push({ label: formatUnitLabel(conv.tgtUnitName, conv.tgtSymbol), value: conv.tgtUnitId });
      }
    }
    return options;
  }

  private markDirty() {
    this.isDirty.set(true);
    this.dirtyChange.emit(true);
    this.measuresChange.emit(this.measures());
  }
}
